import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { DsButton, ModalDialog } from '../ds';
import { AlertIcon, CloseIcon, DeploymentsIcon, RollbackIcon } from '../ds/icons';
import { TOAST_SURFACE } from '../ds/toast';
import {
  ApiV2Error,
  getRollback,
  RollbackDeployment,
  RollbackInfo,
  rollbackApp,
} from '../../utils/apiV2';
import { formatDateTime } from '../../utils/dates';

/**
 * "Roll back to previous": the one rollback there is. The platform keeps the
 * live deployment and the one before it, and this runs the previous image
 * again — no build, a few seconds.
 *
 * Without a previous deployment it shows only the live one: nothing to roll
 * back to, so no previous card and no buttons (the header's close stays).
 *
 * Says plainly what does not go back: the environment variables stay the
 * app's current ones, and the database — data and schema — is not touched.
 */

const DeploymentCard = ({
  deployment,
  tag,
  icon,
}: {
  deployment: RollbackDeployment;
  tag: 'Current' | 'Previous';
  icon: React.ReactNode;
}) => (
  <div className="flex items-center gap-3.5 rounded-card border border-navy-500 bg-navy-800 px-4 py-3.5">
    <span className="flex-none text-navy-200">{icon}</span>
    <span className="flex-1 min-w-0">
      <span className="block truncate font-courrier text-ds-125 text-white">{deployment.label}</span>
      <span className="block mt-1 text-ds-115 text-navy-200">
        Deployed {formatDateTime(new Date(deployment.deployedAt))}
        {deployment.isRollback ? ' · rollback' : ''}
      </span>
    </span>
    <span
      className={`flex-none rounded-full px-2.5 py-0.5 text-ds-105 font-medium ${
        tag === 'Current' ? 'bg-white text-navy-900' : 'border border-navy-400 text-navy-100'
      }`}
    >
      {tag}
    </span>
  </div>
);

const RollbackDialog = ({ appId, deploymentId, open, onClose }: {
  appId: string;
  /** From a row of the Deployments list: rolls back only if it is still the previous one. */
  deploymentId?: string;
  open: boolean;
  onClose: () => void;
}) => {
  const navigate = useNavigate();
  const [info, setInfo] = useState<RollbackInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isRollingBack, setIsRollingBack] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setInfo(null);
    setError(null);
    getRollback(appId)
      .then((result) => !cancelled && setInfo(result))
      .catch((e) => !cancelled && setError(e instanceof ApiV2Error ? e.message : 'The deployments could not be loaded.'));
    return () => {
      cancelled = true;
    };
  }, [appId, open]);

  const confirm = async () => {
    setIsRollingBack(true);
    try {
      const { to, mode } = await rollbackApp(appId, deploymentId);
      toast.success(
        mode === 'REBUILD' ? `Rebuilding ${to.label}` : `Rolling back to ${to.label}`,
        { className: TOAST_SURFACE }
      );
      onClose();
      navigate(`/apps/${appId}`);
    } catch (e) {
      toast.error(
        e instanceof ApiV2Error && e.status > 0 && e.status < 500
          ? e.nextAction ? `${e.message} ${e.nextAction}` : e.message
          : 'The rollback could not start.',
        { className: TOAST_SURFACE }
      );
    } finally {
      setIsRollingBack(false);
    }
  };

  // No previous deployment: the dialog only shows what is live.
  const hasPrevious = !!info?.previous;

  const unavailable =
    info && !info.available && hasPrevious
      ? info.reason === 'IMAGE_GONE'
        ? "The previous deployment's image is no longer available, so it can't be rolled back to. Deploy that code again instead."
        : info.reason === 'IN_PROGRESS'
        ? 'A deployment is in progress. Roll back once it finishes.'
        : 'The previous deployment was a redeploy of the same code, so rolling back would change nothing.'
      : null;

  return (
    <ModalDialog
      open={open}
      onClose={onClose}
      closeButton
      title={!info || hasPrevious ? 'Roll back' : 'Current deployment'}
      width="36rem"
      footer={
        !info || hasPrevious ? (
          <>
            <DsButton variant="outline" size="md" onClick={onClose}>Cancel</DsButton>
            <DsButton
              variant="action"
              size="md"
              isDisabled={!info || !info.available || isRollingBack}
              onClick={confirm}
            >
              {isRollingBack ? 'Rolling back…' : 'Roll back'}
            </DsButton>
          </>
        ) : undefined
      }
    >
      {error ? (
        <div role="alert" className="flex items-center gap-2 text-ds-125 text-state-err">
          <AlertIcon size={14} /> {error}
        </div>
      ) : !info ? (
        <div className="font-courrier text-ds-115 text-navy-200">Loading deployments…</div>
      ) : (
        <div className="flex flex-col gap-3">
          {info.current ? (
            <>
              {hasPrevious ? <div className="text-ds-135 text-navy-100">Rolling back</div> : null}
              <DeploymentCard
                deployment={info.current}
                tag="Current"
                icon={hasPrevious ? <CloseIcon size={18} /> : <DeploymentsIcon size={18} />}
              />
            </>
          ) : !hasPrevious ? (
            <p className="m-0 text-ds-125 leading-[1.55] text-navy-200">No deployment is live yet.</p>
          ) : null}
          {info.previous ? (
            <>
              <div className="text-ds-135 text-navy-100 mt-1">To the following deployment:</div>
              <DeploymentCard deployment={info.previous} tag="Previous" icon={<RollbackIcon size={18} />} />
            </>
          ) : null}

          {!hasPrevious ? null : unavailable ? (
            <p className="m-0 text-ds-125 leading-[1.55] text-navy-200">{unavailable}</p>
          ) : (
            <p className="m-0 text-ds-125 leading-[1.55] text-navy-200">
              {info.available && info.mode === 'REBUILD'
                ? "The previous image is no longer kept, so its code is built again: this takes as long as a deploy. "
                : 'The previous image runs again. '}
              It uses the app's current environment variables. The database is not
              rolled back: its data and schema stay as they are now.
            </p>
          )}
        </div>
      )}
    </ModalDialog>
  );
};

export default RollbackDialog;
